import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    MenuItem,
    Alert,
    CircularProgress,
    useTheme,
    alpha
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PercentIcon from '@mui/icons-material/Percent';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { DataContext } from '../../context/DataContext';

const GirviPayment = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { girvi, updateGirvi, addGirviPayment, loading } = useContext(DataContext);

    const [currentGirvi, setCurrentGirvi] = useState(null);
    const [paymentData, setPaymentData] = useState({
        paymentAmount: '',
        type: 'Interest'
    });
    const [calculations, setCalculations] = useState({
        interest: 0,
        totalPayable: 0,
        principal: 0
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const loadGirviData = useCallback(async () => {
        try {
            const girviData = girvi ? girvi?.find(g => g.id === id) : null;
            if (!girviData) {
                setError('Girvi not found');
                return;
            }

            setCurrentGirvi(girviData);

            // Calculate interest
            const today = new Date();
            let lastCalcDate = new Date(girviData?.interestTillDate);
            let diffDays = Math.ceil((today - lastCalcDate) / (1000 * 60 * 60 * 24));
            const rate = parseFloat(girviData?.interestRate);
            let principal = parseFloat(girviData?.principal);
            let interest = 0;

            while (diffDays >= 365) {
                const yearlyInterest = 12 * (principal * rate / 100);
                principal += yearlyInterest;
                lastCalcDate.setFullYear(lastCalcDate.getFullYear() + 1);
                diffDays = Math.ceil((today - lastCalcDate) / (1000 * 60 * 60 * 24));
            }

            if (diffDays > 0) {
                if (diffDays <= 30) {
                    interest = (principal * rate) / 100;
                } else {
                    const months = Math.floor(diffDays / 30);
                    const extraDays = diffDays % 30;

                    interest = months * (principal * rate / 100);

                    if (extraDays <= 15 && extraDays > 0) {
                        interest += 0.5 * (principal * rate / 100);
                    } else if (extraDays > 15) {
                        interest += (principal * rate / 100);
                    }
                }
            }

            const newTotalPayable = principal + interest;

            setCalculations({
                interest,
                totalPayable: newTotalPayable,
                principal
            });
        } catch (err) {
            setError('Error loading girvi data');
        }
    }, [id, girvi]); // Add dependencies that loadGirviData uses

    useEffect(() => {
        if (id) {
            loadGirviData();
        }
    }, [id, loadGirviData]); // Now both dependencies are stable

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const payment = parseFloat(paymentData.paymentAmount);
            if (isNaN(payment) || payment <= 0) {
                setError('Invalid payment amount');
                return;
            }

            let remainingPrincipal = calculations.principal;
            let newInterest = calculations.interest;
            const type = paymentData.type;

            if (type === 'Interest') {
                if (payment <= calculations.interest) {
                    newInterest = calculations.interest - payment;
                } else {
                    const extra = payment - calculations.interest;
                    newInterest = 0;
                    remainingPrincipal -= extra;
                    if (remainingPrincipal < 0) remainingPrincipal = 0;
                }
            } else if (type === 'Full') {
                remainingPrincipal = 0;
                newInterest = 0;
            }

            // Add payment record
            const paymentRecord = {
                girviId: id,
                date: new Date().toISOString().split('T')[0],
                paymentAmount: payment,
                type: type,
                remainingPrincipal: remainingPrincipal,
                interestEarned: calculations.interest
            };

            await addGirviPayment(paymentRecord);

            // Update girvi
            const updateData = {
                id: id,
                principal: remainingPrincipal,
                interestAmount: newInterest,
                interestTillDate: new Date().toISOString().split('T')[0],
                totalPayable: remainingPrincipal + newInterest,
                customerName: currentGirvi.customerName,
                mobileNo: currentGirvi.mobileNo,
                itemName: currentGirvi.itemName,
                itemWeight: currentGirvi.itemWeight,
                interestRate: currentGirvi.interestRate,
                startDate: new Date(currentGirvi.startDate).toISOString().split('T')[0],
                initialPrincipal: currentGirvi.initialPrincipal
            };

            await updateGirvi(updateData);

            setSuccess('Payment processed successfully! Redirecting to dashboard...');
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
        } catch (err) {
            setError('Error processing payment: ' + err.message);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (!currentGirvi) {
        return (
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
                px: { xs: 2, sm: 3, md: 4 },
                background: isDark
                    ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                    : "linear-gradient(135deg, rgba(224, 209, 209, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%)",
            }}>
                <CircularProgress
                    size={60}
                    thickness={3}
                    sx={{
                        color: isDark ? '#3b82f6' : '#10b981',
                        mb: 3
                    }}
                />
                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500
                    }}
                >
                    Loading Girvi Details...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            py: 4,
            px: { xs: 2, sm: 3, md: 3 },

        }}>
            <Box sx={{ mx: 'auto' }}>
                {/* Back Button */}
                {/* <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        mb: 3,
                        borderRadius: '12px',
                        color: 'text.primary',
                        borderColor: isDark 
                            ? 'rgba(148, 163, 184, 0.1)' 
                            : 'rgba(226, 232, 240, 0.6)',
                        backgroundColor: isDark 
                            ? 'rgba(30, 41, 59, 0.3)' 
                            : 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: isDark 
                                ? 'rgba(30, 41, 59, 0.5)' 
                                : 'rgba(255, 255, 255, 0.5)',
                            transform: 'translateX(-4px)',
                        }
                    }}
                    variant="outlined"
                >
                    Back to Dashboard
                </Button> */}

                {/* Main Payment Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, sm: 4 },
                        backgroundColor: isDark
                            ? 'rgba(30, 41, 59, 0.4)'
                            : 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${isDark
                            ? 'rgba(148, 163, 184, 0.1)'
                            : 'rgba(255, 255, 255, 0.2)'
                            }`,
                        borderRadius: '24px',
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0, 0, 0, 0.2)'
                            : '0 8px 32px rgba(31, 38, 135, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: isDark
                                ? '0 12px 40px rgba(0, 0, 0, 0.3)'
                                : '0 12px 40px rgba(31, 38, 135, 0.15)',
                        }
                    }}
                >
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isDark
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                boxShadow: isDark
                                    ? '0 4px 20px rgba(59, 130, 246, 0.3)'
                                    : '0 4px 20px rgba(16, 185, 129, 0.3)'
                            }}>
                                <PaymentIcon sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 800,
                                        background: isDark
                                            ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
                                            : 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        fontSize: { xs: '1.8rem', sm: '2.2rem' }
                                    }}
                                >
                                    Girvi Payment
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    Process payment for {currentGirvi.customerName}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Alerts */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: '12px',
                                backgroundColor: isDark
                                    ? alpha('#ef4444', 0.1)
                                    : alpha('#ef4444', 0.1),
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${alpha('#ef4444', 0.2)}`,
                                color: '#ef4444',
                                '& .MuiAlert-icon': {
                                    color: '#ef4444'
                                }
                            }}
                            onClose={() => setError('')}
                        >
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert
                            severity="success"
                            sx={{
                                mb: 3,
                                borderRadius: '12px',
                                backgroundColor: isDark
                                    ? alpha('#10b981', 0.1)
                                    : alpha('#10b981', 0.1),
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${alpha('#10b981', 0.2)}`,
                                color: '#10b981',
                                '& .MuiAlert-icon': {
                                    color: '#10b981'
                                }
                            }}
                            onClose={() => setSuccess('')}
                        >
                            {success}
                        </Alert>
                    )}

                    {/* Summary Cards */}
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 2,
                                fontSize: '1.1rem'
                            }}
                        >
                            Current Balance Summary
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Principal */}
                            <Grid item xs={12} sm={4}>
                                <Box sx={{
                                    p: 3,
                                    backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.5)'
                                        : 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                        }`,
                                    borderRadius: '16px',
                                    boxShadow: isDark
                                        ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                        : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: isDark
                                            ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                            : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                    }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isDark
                                                ? alpha('#3b82f6', 0.1)
                                                : alpha('#10b981', 0.1),
                                            color: isDark ? '#3b82f6' : '#10b981'
                                        }}>
                                            <AccountBalanceWalletIcon />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600
                                            }}
                                        >
                                            Principal
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            color: isDark ? '#3b82f6' : '#10b981',
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {formatCurrency(calculations.principal)}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Interest */}
                            <Grid item xs={12} sm={4}>
                                <Box sx={{
                                    p: 3,
                                    backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.5)'
                                        : 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                        }`,
                                    borderRadius: '16px',
                                    boxShadow: isDark
                                        ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                        : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: isDark
                                            ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                            : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                    }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isDark
                                                ? alpha('#8b5cf6', 0.1)
                                                : alpha('#8b5cf6', 0.1),
                                            color: '#8b5cf6'
                                        }}>
                                            <PercentIcon />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600
                                            }}
                                        >
                                            Interest
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            color: '#8b5cf6',
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {formatCurrency(calculations.interest)}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Total Payable */}
                            <Grid item xs={12} sm={4}>
                                <Box sx={{
                                    p: 3,
                                    backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.5)'
                                        : 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                        }`,
                                    borderRadius: '16px',
                                    boxShadow: isDark
                                        ? '0 4px 20px rgba(0, 0, 0, 0.15)'
                                        : '0 4px 20px rgba(31, 38, 135, 0.08)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: isDark
                                            ? '0 8px 30px rgba(0, 0, 0, 0.25)'
                                            : '0 8px 30px rgba(31, 38, 135, 0.12)',
                                    }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isDark
                                                ? alpha('#f59e0b', 0.1)
                                                : alpha('#f59e0b', 0.1),
                                            color: '#f59e0b'
                                        }}>
                                            <AttachMoneyIcon />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600
                                            }}
                                        >
                                            Total Payable
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            color: '#f59e0b',
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {formatCurrency(calculations.totalPayable)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Additional Info */}
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.3)'
                                        : 'rgba(255, 255, 255, 0.4)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                        }`,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}>
                                    <CalendarTodayIcon sx={{
                                        color: isDark ? '#94a3b8' : '#718096',
                                        fontSize: 20
                                    }} />
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                        }}>
                                            Interest Calculation Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {new Date(currentGirvi?.interestTillDate).toLocaleDateString("en-US")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: isDark
                                        ? 'rgba(30, 41, 59, 0.3)'
                                        : 'rgba(255, 255, 255, 0.4)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${isDark
                                        ? 'rgba(148, 163, 184, 0.1)'
                                        : 'rgba(255, 255, 255, 0.2)'
                                        }`,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}>
                                    <PercentIcon sx={{
                                        color: isDark ? '#94a3b8' : '#718096',
                                        fontSize: 20
                                    }} />
                                    <Box>
                                        <Typography variant="caption" sx={{
                                            color: 'text.secondary',
                                            display: 'block'
                                        }}>
                                            Interest Rate
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {currentGirvi?.interestRate}%
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Payment Form */}
                    <form onSubmit={handleSubmit}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                backgroundColor: isDark
                                    ? 'rgba(30, 41, 59, 0.3)'
                                    : 'rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(20px)',
                                border: `1px solid ${isDark
                                    ? 'rgba(148, 163, 184, 0.1)'
                                    : 'rgba(255, 255, 255, 0.2)'
                                    }`,
                                borderRadius: '16px',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    mb: 3,
                                    fontSize: '1.1rem'
                                }}
                            >
                                Payment Details
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Payment Amount */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Payment Amount"
                                        name="paymentAmount"
                                        type="text"
                                        value={paymentData.paymentAmount}
                                        onChange={handlePaymentChange}
                                        required
                                        placeholder="Enter payment amount"
                                        InputProps={{
                                            startAdornment: (
                                                <Box sx={{
                                                    mr: 1,
                                                    color: isDark ? '#94a3b8' : '#718096'
                                                }}>
                                                    ₹
                                                </Box>
                                            ),
                                            sx: {
                                                borderRadius: '12px',
                                                backgroundColor: isDark
                                                    ? 'rgba(30, 41, 59, 0.5)'
                                                    : 'rgba(255, 255, 255, 0.7)',
                                                backdropFilter: 'blur(10px)',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark
                                                        ? 'rgba(148, 163, 184, 0.1)'
                                                        : 'rgba(226, 232, 240, 0.6)',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark ? '#3b82f6' : '#10b981',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark ? '#3b82f6' : '#10b981',
                                                    borderWidth: '2px',
                                                }
                                            }
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                color: 'text.secondary',
                                                '&.Mui-focused': {
                                                    color: isDark ? '#3b82f6' : '#10b981',
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                {/* Payment Type */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Payment Type"
                                        name="type"
                                        value={paymentData.type}
                                        onChange={handlePaymentChange}
                                        InputProps={{
                                            sx: {
                                                borderRadius: '12px',
                                                backgroundColor: isDark
                                                    ? 'rgba(30, 41, 59, 0.5)'
                                                    : 'rgba(255, 255, 255, 0.7)',
                                                backdropFilter: 'blur(10px)',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark
                                                        ? 'rgba(148, 163, 184, 0.1)'
                                                        : 'rgba(226, 232, 240, 0.6)',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark ? '#3b82f6' : '#10b981',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: isDark ? '#3b82f6' : '#10b981',
                                                    borderWidth: '2px',
                                                }
                                            }
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                color: 'text.secondary',
                                                '&.Mui-focused': {
                                                    color: isDark ? '#3b82f6' : '#10b981',
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem
                                            value="Interest"
                                            sx={{
                                                color: 'text.primary',
                                                '&:hover': {
                                                    backgroundColor: isDark
                                                        ? alpha('#3b82f6', 0.1)
                                                        : alpha('#10b981', 0.1),
                                                }
                                            }}
                                        >
                                            Interest Only
                                        </MenuItem>
                                        <MenuItem
                                            value="Full"
                                            sx={{
                                                color: 'text.primary',
                                                '&:hover': {
                                                    backgroundColor: isDark
                                                        ? alpha('#3b82f6', 0.1)
                                                        : alpha('#10b981', 0.1),
                                                }
                                            }}
                                        >
                                            Full Payment
                                        </MenuItem>
                                    </TextField>
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={loading}
                                        startIcon={loading ? null : <PaymentIcon />}
                                        sx={{
                                            py: 1.5,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            background: isDark
                                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            boxShadow: isDark
                                                ? '0 4px 14px 0 rgba(59, 130, 246, 0.35)'
                                                : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                background: isDark
                                                    ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                                boxShadow: isDark
                                                    ? '0 6px 20px 0 rgba(59, 130, 246, 0.45)'
                                                    : '0 6px 20px 0 rgba(16, 185, 129, 0.45)',
                                                transform: 'translateY(-2px)'
                                            },
                                            '&:disabled': {
                                                background: isDark
                                                    ? 'rgba(148, 163, 184, 0.3)'
                                                    : 'rgba(226, 232, 240, 0.6)',
                                                color: isDark
                                                    ? 'rgba(148, 163, 184, 0.5)'
                                                    : 'rgba(100, 116, 139, 0.5)',
                                            }
                                        }}
                                    >
                                        {loading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={20} sx={{ color: 'white' }} />
                                                Processing Payment...
                                            </Box>
                                        ) : 'Submit Payment'}
                                    </Button>
                                </Grid>

                                {/* Note */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            display: 'block',
                                            textAlign: 'center',
                                            opacity: 0.7
                                        }}
                                    >
                                        {paymentData.type === 'Interest'
                                            ? 'Interest payment will reduce the pending interest amount first'
                                            : 'Full payment will clear both principal and interest'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </form>
                </Paper>
            </Box>
        </Box>
    );
};

export default GirviPayment;